import {
  Ban,
  Bell,
  BellOff,
  CheckCircle2,
  Loader2,
  Share,
  Smartphone,
} from 'lucide-react'
import { toast } from 'sonner'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import { apiErrorMessage } from '@/lib/api'
import { subscribeToPush, unsubscribeFromPush } from '@/lib/push'
import {
  usePushSubscription,
  useRegisterPushSubscription,
  useTestPush,
  useUnregisterPushSubscription,
  useVapidPublicKey,
} from '@/features/admin/use-push'

export default function NotificationsSettings() {
  const { capability, hasSubscription, loading, refresh } = usePushSubscription()
  const { data: vapidKey, isLoading: vapidLoading, error: vapidError } =
    useVapidPublicKey()
  const registerMutation = useRegisterPushSubscription()
  const unregisterMutation = useUnregisterPushSubscription()
  const testMutation = useTestPush()

  async function handleSubscribe() {
    if (!vapidKey) {
      toast.error('Brak klucza VAPID', {
        description: 'Powiadomienia nie są skonfigurowane na serwerze.',
      })
      return
    }
    try {
      if (capability.permission === 'default') {
        const result = await Notification.requestPermission()
        if (result !== 'granted') {
          await refresh()
          if (result === 'denied') {
            toast.info('Powiadomienia zablokowane')
          }
          return
        }
      }
      const sub = await subscribeToPush(vapidKey)
      await registerMutation.mutateAsync(sub)
      await refresh()
      toast.success('Powiadomienia włączone')
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name
      if (name === 'AbortError') return
      if (name === 'NotAllowedError') {
        toast.info('Powiadomienia zablokowane')
        await refresh()
        return
      }
      toast.error('Nie udało się włączyć powiadomień', {
        description: apiErrorMessage(err),
      })
    }
  }

  async function handleUnsubscribe() {
    try {
      const endpoint = await unsubscribeFromPush()
      if (endpoint) {
        await unregisterMutation.mutateAsync(endpoint)
      }
      await refresh()
      toast.success('Powiadomienia wyłączone')
    } catch (err) {
      toast.error('Nie udało się wyłączyć', {
        description: apiErrorMessage(err),
      })
    }
  }

  function handleTestPush() {
    testMutation.mutate(undefined, {
      onSuccess: (data) =>
        toast.success(`Push wysłany (sent: ${data.sent}, removed: ${data.removed})`),
      onError: (err) =>
        toast.error('Test push nie powiódł się', {
          description: apiErrorMessage(err),
        }),
    })
  }

  if (loading || vapidLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-text-muted">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        <span className="ml-2 text-sm">Ładuję ustawienia powiadomień…</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text-primary">
          Powiadomienia push
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          Po włączeniu otrzymasz powiadomienie systemowe gdy sesja pracownika
          zostanie rozpoczęta lub zakończona poza obszarem placówki.
        </p>
      </div>

      {vapidError && (
        <Alert variant="error">
          Nie udało się pobrać klucza VAPID z serwera. Spróbuj odświeżyć
          stronę.
        </Alert>
      )}

      {!capability.supported && <UnsupportedBrowserCard />}

      {capability.supported && capability.iosStandaloneRequired && (
        <IosA2hsInstructionCard />
      )}

      {capability.supported &&
        !capability.iosStandaloneRequired &&
        capability.permission === 'denied' && <BlockedInBrowserCard />}

      {capability.supported &&
        !capability.iosStandaloneRequired &&
        capability.permission !== 'denied' && (
          <SubscriptionPanel
            hasSubscription={hasSubscription}
            onSubscribe={handleSubscribe}
            onUnsubscribe={handleUnsubscribe}
            onTestPush={handleTestPush}
            subscribing={registerMutation.isPending}
            unsubscribing={unregisterMutation.isPending}
            testing={testMutation.isPending}
            vapidReady={Boolean(vapidKey)}
          />
        )}
    </div>
  )
}

function SubscriptionPanel({
  hasSubscription,
  onSubscribe,
  onUnsubscribe,
  onTestPush,
  subscribing,
  unsubscribing,
  testing,
  vapidReady,
}: {
  hasSubscription: boolean
  onSubscribe: () => void
  onUnsubscribe: () => void
  onTestPush: () => void
  subscribing: boolean
  unsubscribing: boolean
  testing: boolean
  vapidReady: boolean
}) {
  if (!hasSubscription) {
    return (
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6">
        <div className="flex items-start gap-3">
          <BellOff
            className="h-5 w-5 text-text-faint mt-0.5 flex-shrink-0"
            aria-hidden
          />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-text-primary">
              Powiadomienia są wyłączone
            </h3>
            <p className="mt-1 text-sm text-text-muted">
              Włącz aby otrzymywać alerty o sesjach poza obszarem placówki.
              Po kliknięciu przeglądarka zapyta o pozwolenie.
            </p>
          </div>
        </div>
        <div className="mt-4">
          <Button
            onClick={onSubscribe}
            disabled={subscribing || !vapidReady}
            loading={subscribing}
          >
            <Bell className="h-4 w-4" aria-hidden />
            Włącz powiadomienia
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-success/30 bg-success/5 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle2
            className="h-5 w-5 text-success mt-0.5 flex-shrink-0"
            aria-hidden
          />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-success">
              Powiadomienia aktywne
            </h3>
            <p className="mt-1 text-sm text-text-muted">
              Otrzymasz alert gdy sesja pracownika będzie rozpoczęta lub
              zakończona poza obszarem placówki.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          onClick={onTestPush}
          disabled={testing}
          loading={testing}
        >
          <Bell className="h-4 w-4" aria-hidden />
          Wyślij testowy push
        </Button>
        <Button
          variant="ghost"
          onClick={onUnsubscribe}
          disabled={unsubscribing}
          loading={unsubscribing}
          className="text-danger hover:text-danger hover:bg-danger/5"
        >
          <BellOff className="h-4 w-4" aria-hidden />
          Wyłącz powiadomienia
        </Button>
      </div>
    </div>
  )
}

function UnsupportedBrowserCard() {
  return (
    <Alert variant="info">
      <div className="space-y-1">
        <div className="font-medium">
          Twoja przeglądarka nie wspiera powiadomień push.
        </div>
        <div className="text-xs">
          Otwórz aplikację w aktualnej wersji Chrome, Safari (16.4+) lub Firefox.
        </div>
      </div>
    </Alert>
  )
}

function IosA2hsInstructionCard() {
  return (
    <div className="rounded-card border border-warning/30 bg-warning/5 p-4">
      <div className="flex items-start gap-3">
        <Smartphone
          className="h-5 w-5 text-warning mt-0.5 flex-shrink-0"
          aria-hidden
        />
        <div className="flex-1 space-y-2">
          <h3 className="text-sm font-medium text-text-primary">
            iPhone wymaga dodania aplikacji do ekranu domowego
          </h3>
          <p className="text-sm text-text-muted">
            Powiadomienia push na iPhone działają tylko w trybie standalone
            (PWA dodane do ekranu domowego). Aby je włączyć:
          </p>
          <ol className="list-decimal pl-5 text-sm text-text-muted space-y-1">
            <li>Otwórz aplikację w Safari (nie w innej przeglądarce).</li>
            <li>
              Tap ikonę <Share className="inline h-3.5 w-3.5" aria-hidden />{' '}
              „Udostępnij" na dolnym pasku.
            </li>
            <li>Wybierz „Dodaj do ekranu domowego".</li>
            <li>Otwórz aplikację z nowej ikony na ekranie domowym.</li>
            <li>Wróć tutaj i włącz powiadomienia.</li>
          </ol>
        </div>
      </div>
    </div>
  )
}

function BlockedInBrowserCard() {
  return (
    <div className="rounded-card border border-danger/30 bg-danger/5 p-4">
      <div className="flex items-start gap-3">
        <Ban
          className="h-5 w-5 text-danger mt-0.5 flex-shrink-0"
          aria-hidden
        />
        <div className="flex-1 space-y-2">
          <h3 className="text-sm font-medium text-text-primary">
            Powiadomienia zablokowane w przeglądarce
          </h3>
          <p className="text-sm text-text-muted">
            Aby włączyć powiadomienia, najpierw zezwól na nie w ustawieniach
            przeglądarki:
          </p>
          <ul className="list-disc pl-5 text-sm text-text-muted space-y-1">
            <li>
              <strong>Chrome / Edge:</strong> kliknij ikonę kłódki w pasku
              adresu → Powiadomienia → „Zezwól".
            </li>
            <li>
              <strong>Safari:</strong> Preferencje → Witryny → Powiadomienia →
              znajdź czas.dentalavenue.pl → „Zezwól".
            </li>
            <li>
              <strong>Firefox:</strong> kliknij ikonę kłódki → Powiadomienia →
              usuń blokadę.
            </li>
          </ul>
          <p className="text-xs text-text-faint">
            Po zmianie odśwież stronę.
          </p>
        </div>
      </div>
    </div>
  )
}
